import { useState, useEffect } from 'react';
import { Modal, Table, Spin } from 'antd';
import { IFarmResource, IFarmStatus } from '../../entities/farms.entities';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  Subtitle,
} from '../shared';
import moment from 'moment';
import { sendSingleRequest } from '../../apis';
import { useDispatch, useSelector } from 'react-redux';
import { showFeedback } from '../../store/ui/ui.actions';
import { loadFarmsData } from '../../store/farms/farms.actions';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import { translate } from '../../lib/lang.helper';
import { selectLang } from '../../store/ui/ui.selector';
import { defaultDateFormat, calcDiffDays } from '../../lib/common.helpers';
import { selectUtils } from '../../store/utils/utils.selector';

const columns = (lang: any) => [
  {
    key: 'created_at',
    dataIndex: 'created_at',
    title: translate(lang, 'Closed Date'),
    render: (x: any) => <span>{defaultDateFormat(x)}</span>,
  },
  {
    key: 'due_date',
    dataIndex: 'due_date',
    title: translate(lang, 'Closed Until'),
    render: (x: any) => <span>{defaultDateFormat(x)}</span>,
  },
  {
    key: 'content',
    dataIndex: 'content',
    title: translate(lang, 'Comment'),
    render: (x: any) => <span>{x ?? ''}</span>,
  },
  {
    key: 'status',
  },
];

interface Props {
  farm: IFarmResource;
  className?: string;
  onClose: () => void;
}

const FarmStatusModal = ({ farm, onClose, className }: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const commentTemplates = useSelector(selectUtils)
    .filter(x => x.type === 'comment_template')
    .map(x => x.name);

  const toLabel = (s: string) => (s.length > 55 ? `${s.slice(0, 55)}...` : s);

  const [statuses, setStatuses] = useState<IFarmStatus[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [date, setDate] = useState<number | null>(Date.now());
  const [content, setContent] = useState('');

  useEffect(() => {
    setLoadingStatus(true);
    sendSingleRequest({}, 'GET', `api/farm/farm-status/${farm.id}`, true).then(
      res => {
        setLoadingStatus(false);
        if (res.status) {
          setStatuses(res.data ?? []);
        }
      },
    );
  }, [farm.id]);

  const currentStatus = [...statuses]
    .sort((a, b) => b.created_at - a.created_at)
    .find(s => s.due_date === null || s.due_date * 1000 > Date.now());
  const isClosed = !!currentStatus;

  const updateDate = (d: number | null) => {
    if (d === null) {
      setDate(null);
      return;
    }
    const min = currentStatus?.due_date;
    if (min && d < min * 1000) {
      dispatch(
        showFeedback({
          isMessageModal: true,
          type: 'error',
          message: translate(
            lang,
            '_min_allowed_date',
            defaultDateFormat(min),
          ),
        }),
      );
      return;
    }
    if (d < moment().add(1, 'days').toDate().getTime()) {
      dispatch(
        showFeedback({
          isMessageModal: true,
          type: 'error',
          message: translate(
            lang,
            '_min_allowed_date',
            defaultDateFormat(moment().add(1, 'days').toDate().getTime()),
          ),
        }),
      );
      return;
    }
    setDate(d);
  };
  const closeModal = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    onClose();
  };
  const afterClick = async (res: any) => {
    if (res.status) {
      await dispatch(loadFarmsData());
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, 'Success'),
        }),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, res.data?.message ?? 'Failed'),
        }),
      );
    }
    onClose();
  };
  const closeClick = async (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    setDisabled(true);
    const res = await sendSingleRequest(
      {
        status: isClosed ? 'extend' : 'close',
        farm_id: farm.id,
        due_date: date
          ? new Date(date).toISOString().slice(0, 19).replace('T', ' ')
          : null,
        content: content || null,
      },
      'POST',
      'api/farm/farm-status',
      true,
    );
    afterClick(res);
  };
  const openClick = async (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    setDisabled(true);
    const res = await sendSingleRequest(
      {
        status: 'open',
        farm_id: farm.id,
        due_date: date
          ? new Date(date).toISOString().slice(0, 19).replace('T', ' ')
          : null,
        content: content || null,
      },
      'POST',
      'api/farm/farm-status',
      true,
    );
    afterClick(res);
  };

  return (
    <Modal
      visible={true}
      onCancel={closeModal}
      className={className}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={600}
    >
      <div className='wrap'>
        <div className='d-flex align-center justify-center mb-16'>
          <Subtitle color='black-1' align='center' size={1} fontWeight={600}>
            {translate(lang, 'Farm %s status', farm.name)}
          </Subtitle>
        </div>
        {loadingStatus ? (
          <div className='d-flex justify-center mt-16 mb-16'>
            <Spin />
          </div>
        ) : (
          <>
            <div className='mt-16 mb-16'>
              <Subtitle
                color='black-1'
                align='center'
                size={0}
                fontWeight={500}
              >
                {!isClosed
                  ? translate(lang, '%s is open', farm.name)
                  : currentStatus.due_date
                    ? translate(
                        lang,
                        '%s is closed at %s, for %s days.',
                        farm.name,
                        defaultDateFormat(currentStatus.created_at),
                        calcDiffDays(Date.now(), currentStatus.due_date),
                      )
                    : translate(lang, '%s is closed indefinitely.', farm.name)}
              </Subtitle>
            </div>
            {statuses.length > 0 && (
              <div className='mt-17'>
                <Table
                  className='table'
                  rowKey='id'
                  pagination={false}
                  columns={columns(lang)}
                  dataSource={statuses}
                />
              </div>
            )}
          </>
        )}
        <div className='pb-24'>
          <CheckboxButton
            className='mb-17'
            label={translate(lang, '_reopened')}
            checked={!date}
            onChange={e => {
              if (e.target.checked) {
                setDate(null);
              } else {
                setDate(Date.now());
              }
            }}
          />
          {!!date && (
            <Datepicker
              className='mb-17'
              defaultValue={date}
              label={translate(lang, 'Close until')}
              onChange={e => updateDate(e ? e.toDate().getTime() : null)}
            />
          )}
          {commentTemplates.length > 0 && (
            <Dropdown
              label={translate(lang, 'Select a sentence')}
              options={commentTemplates.map((template, id) => ({
                id: id.toString(),
                label: toLabel(template),
                value: template,
              }))}
              onChange={value => setContent(value)}
            />
          )}
          <div className='w-100 mb-17'>
            <Input
              type='textarea'
              label={translate(lang, 'Comment')}
              value={content}
              onChange={e => setContent(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className='modal-button d-flex justify-center align-center'>
        {isClosed && (
          <Button
            className='rsp-btn ml-17 mr-17'
            width='small'
            size={2}
            type='fill'
            color='green'
            onClick={openClick}
            disabled={disabled}
          >
            {translate(lang, 'Open Farm')}
          </Button>
        )}
        <Button
          className='rsp-btn ml-17 mr-17'
          width='small'
          size={2}
          type='fill'
          color='red'
          onClick={closeClick}
          disabled={disabled}
        >
          {translate(lang, isClosed ? 'Extend close period' : 'Close Farm')}
        </Button>
        <Button
          className='rsp-btn ml-17 mr-17'
          width='small'
          size={2}
          type='fill'
          color='blue'
          onClick={closeModal}
          disabled={disabled}
        >
          {translate(lang, 'Cancel')}
        </Button>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default FarmStatusModal;
